import { Injectable } from '@angular/core';
import { Bill, BillItem, BillPerson, BillValidationResult } from './bill-splitter.models';

export type ChargeKey = 'tax' | 'serviceCharge' | 'tip' | 'discount';
export const CHARGE_KEYS: ChargeKey[] = ['tax', 'serviceCharge', 'tip', 'discount'];

@Injectable({ providedIn: 'root' })
export class BillSplitterCalculatorService {
  toMinor(value: number): number {
    if (!Number.isFinite(value) || Math.abs(value) > 1e9) throw new Error('Enter a valid amount.');
    return Math.round((value + Number.EPSILON) * 100);
  }
  lineTotal(quantity: number, unitPrice: number): number { return Math.round(quantity * unitPrice); }
  calculateItemDiscount(item: BillItem): number {
    return this.lineTotal(item.quantity, item.unitDiscount ?? 0) + (item.discount ?? 0);
  }
  /** A negative receipt line is a refund or deposit-return credit, not a malformed price. */
  calculateItemNet(item: BillItem): number {
    return item.total < 0 ? item.total : Math.max(0, item.total - this.calculateItemDiscount(item));
  }
  calculateSubtotal(bill: Bill): number { return bill.items.reduce((s, i) => s + this.calculateItemNet(i), 0); }
  calculateGrossSubtotal(bill: Bill): number { return bill.items.reduce((s, i) => s + i.total, 0); }
  calculateDiscount(bill: Bill): number {
    return bill.items.reduce((s, i) => s + this.calculateItemDiscount(i), 0) + this.chargeAmount(bill, 'discount');
  }

  /** No explicit owners means the line follows the current list of people. */
  isShared(item: BillItem, people: BillPerson[]): boolean {
    return !item.allocations.some((a) => a.share > 0 && people.some((p) => p.id === a.personId));
  }
  calculateItemContribution(item: BillItem, personId: string, people?: BillPerson[]): number {
    const allowed = people ?? item.allocations.map((a) => ({ id: a.personId, name: '' }));
    const weights = allowed.map((p) => ({
      id: p.id,
      weight: item.allocations.filter((a) => a.personId === p.id && Number.isFinite(a.share) && a.share > 0).reduce((s, a) => s + a.share, 0),
    }));
    if (!weights.some((w) => w.weight > 0)) weights.forEach((w) => w.weight = 1);
    return this.distribute(this.calculateItemNet(item), weights).get(personId) ?? 0;
  }
  calculatePersonItems(bill: Bill, personId: string): number {
    return bill.items.reduce((s, i) => s + this.calculateItemContribution(i, personId, bill.people), 0);
  }
  calculatePersonTotal(bill: Bill, personId: string): number {
    if (!bill.people.some((p) => p.id === personId)) return 0;
    return this.calculatePersonItems(bill, personId) + CHARGE_KEYS.reduce((s, key) =>
      s + (key === 'discount' ? -1 : 1) * this.chargeContribution(bill, key, personId), 0);
  }
  chargeAmount(bill: Bill, key: ChargeKey): number {
    const charge = bill[key];
    return key === 'discount' && charge.percentage != null
      ? Math.round(this.calculateSubtotal(bill) * charge.percentage / 100) : charge.amount;
  }
  chargeContribution(bill: Bill, key: ChargeKey, personId: string): number {
    const charge = bill[key];
    const amount = this.chargeAmount(bill, key);
    if (charge.strategy === 'specific-person') return charge.personId === personId ? amount : 0;
    const hasBase = this.calculateSubtotal(bill) > 0;
    return this.distribute(amount, bill.people.map((p) => ({ id: p.id,
      weight: charge.strategy === 'equal' || !hasBase ? 1 : this.calculatePersonItems(bill, p.id),
    }))).get(personId) ?? 0;
  }
  calculateTotal(bill: Bill): number {
    return this.calculateSubtotal(bill) + CHARGE_KEYS.reduce((s, key) => s + (key === 'discount' ? -1 : 1) * this.chargeAmount(bill, key), 0);
  }
  calculateAllocatedTotal(bill: Bill): number { return bill.people.reduce((s, p) => s + this.calculatePersonTotal(bill, p.id), 0); }
  calculateUnallocatedTotal(bill: Bill): number { return this.calculateTotal(bill) - this.calculateAllocatedTotal(bill); }
  calculateSharedTotal(bill: Bill): number {
    return bill.items.filter((i) => this.isShared(i, bill.people)).reduce((s, i) => s + this.calculateItemNet(i), 0);
  }

  validateBill(bill: Bill): BillValidationResult {
    const warnings: string[] = [...(bill.extractionWarnings ?? [])];
    const itemSubtotal = this.calculateSubtotal(bill);
    const calculatedTotal = this.calculateTotal(bill);
    const money = (v: number) => new Intl.NumberFormat('en', { style: 'currency', currency: bill.currency }).format(v / 100);
    if (bill.subtotal != null && bill.subtotal !== itemSubtotal && bill.subtotal !== this.calculateGrossSubtotal(bill)) {
      warnings.push('Receipt subtotal ' + money(bill.subtotal) + ' does not match the items. Please check prices and discounts.');
    }
    const difference = bill.statedTotal == null ? 0 : bill.statedTotal - calculatedTotal;
    if (difference) warnings.push('Receipt total ' + money(bill.statedTotal!) + ' · calculated ' + money(calculatedTotal) + ' · difference ' + money(Math.abs(difference)) + '.');
    if (!bill.people.length && bill.items.length) warnings.push('Add people to split this bill.');
    for (const item of bill.items) {
      if (!Number.isSafeInteger(item.total) || !Number.isFinite(item.quantity) || item.quantity <= 0) warnings.push('Check the quantity and price of ' + item.name + '.');
      if (item.total >= 0 && this.calculateItemDiscount(item) > item.total) warnings.push('Discounts on ' + item.name + ' exceed its price.');
      if (item.confidence != null && item.confidence < .7) warnings.push('Please review ' + item.name + '; its receipt details were unclear.');
    }
    for (const key of CHARGE_KEYS) {
      const charge = bill[key];
      if (this.chargeAmount(bill, key) && charge.strategy === 'specific-person' && !bill.people.some((p) => p.id === charge.personId)) warnings.push('Choose who receives the ' + key + ' adjustment.');
    }
    if (this.chargeAmount(bill, 'discount') > itemSubtotal) warnings.push('The bill discount exceeds the item subtotal.');
    if (bill.people.some((p) => this.calculatePersonTotal(bill, p.id) < 0)) warnings.push('A person’s discount exceeds their costs. Adjust the discount split.');
    if (bill.people.length && this.calculateUnallocatedTotal(bill)) warnings.push('The person totals do not yet match the bill total.');
    return { valid: warnings.length === 0, warnings, itemSubtotal, calculatedTotal, difference };
  }

  /** Stable largest remainders: no lost cents, including discounts. */
  private distribute(amount: number, weights: Array<{ id: string; weight: number }>): Map<string, number> {
    const positive = weights.filter((w) => Number.isFinite(w.weight) && w.weight > 0);
    const totalWeight = positive.reduce((s, w) => s + w.weight, 0);
    if (!totalWeight) return new Map();
    const rows = positive.map((w, index) => {
      const exact = Math.abs(amount) * w.weight / totalWeight;
      return { ...w, index, floor: Math.floor(exact), remainder: exact - Math.floor(exact) };
    });
    const left = Math.abs(amount) - rows.reduce((s, r) => s + r.floor, 0);
    rows.sort((a, b) => b.remainder - a.remainder || a.index - b.index);
    return new Map(rows.map((r, i) => [r.id, Math.sign(amount) * (r.floor + (i < left ? 1 : 0))]));
  }
}
